import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { StripSegment } from '@bb/shared/core';
import { WEEKDAYS_SHORT, weekday } from './period';

const MONTH_TITLES = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'];

/**
 * Графики без библиотек: SVG + CSS-переменные темы.
 * Цвета серий — --c-feed / --c-sleep / --c-diaper / --c-walk (палитра проверена на различимость
 * при нарушениях цветового зрения в обеих темах). Подписи — цветами текста, не серий.
 */

export type Cat = 'sleep' | 'feed' | 'diaper' | 'walk' | 'awake' | 'cry' | 'other';
const color = (cat: Cat) => `var(--c-${cat})`;

function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [w, setW] = useState(320);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setW(el.clientWidth);
    const ro = new ResizeObserver(() => setW(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** «Красивый» шаг сетки: 1, 2, 5 × 10ⁿ. */
function niceMax(v: number, ticks = 3): { max: number; step: number } {
  if (v <= 0) return { max: ticks, step: 1 };
  const raw = v / ticks;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((k) => k * pow).find((s) => s >= raw) ?? 10 * pow;
  return { max: Math.ceil(v / step) * step, step };
}

/* ---------- Столбцы по дням / месяцам ---------- */

export interface Bar {
  key: string;
  /** Подпись под столбцом (может быть пустой — показываем не все). */
  label: string;
  value: number | null;
  /** Подсветить подпись (сегодня). */
  strong?: boolean;
}

export function BarChart({
  bars,
  cat,
  avg,
  format,
  short,
  selected,
  onSelect,
  height = 168,
  stepMin,
}: {
  bars: Bar[];
  cat: Cat;
  avg: number | null;
  format: (v: number) => string;
  short: (v: number) => string;
  selected: string | null;
  onSelect: (key: string | null) => void;
  height?: number;
  /** Для минут — шаг сетки кратный часу. */
  stepMin?: number;
}) {
  const [ref, w] = useWidth<HTMLDivElement>();
  const padL = 34;
  const padB = 22;
  const padT = 8;
  const plotW = Math.max(10, w - padL);
  const plotH = height - padB - padT;
  const values = bars.map((b) => b.value ?? 0);
  let { max, step } = niceMax(Math.max(...values, avg ?? 0, 1));
  if (stepMin && max > stepMin) {
    step = Math.max(stepMin, Math.ceil(step / stepMin) * stepMin);
    max = Math.ceil(Math.max(...values, avg ?? 0, 1) / step) * step;
  }
  const n = bars.length;
  const slot = plotW / n;
  const gap = n > 20 ? 2 : n > 8 ? 4 : 10;
  const bw = Math.max(2, slot - gap);
  const y = (v: number) => padT + plotH - (v / max) * plotH;
  const labelEvery = n > 20 ? Math.ceil(n / 6) : 1;
  const ticks: number[] = [];
  for (let t = 0; t <= max + 1e-9; t += step) ticks.push(t);

  return (
    <div ref={ref} className="chart" style={{ height }}>
      <svg width={w} height={height} role="img" aria-label="Столбчатая диаграмма по дням">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={w} y1={y(t)} y2={y(t)} className="grid" />
            <text x={padL - 6} y={y(t) + 4} textAnchor="end" className="axis">
              {short(t)}
            </text>
          </g>
        ))}
        {bars.map((b, i) => {
          const v = b.value;
          const x = padL + i * slot + (slot - bw) / 2;
          const top = v ? y(v) : y(0);
          const h = Math.max(0, y(0) - top);
          const r = Math.min(4, bw / 2, h);
          const dim = selected !== null && selected !== b.key;
          return (
            <g key={b.key}>
              {v != null && v > 0 && (
                <path
                  d={`M${x},${y(0)} V${top + r} Q${x},${top} ${x + r},${top} H${x + bw - r} Q${x + bw},${top} ${x + bw},${top + r} V${y(0)} Z`}
                  fill={color(cat)}
                  opacity={dim ? 0.35 : 1}
                />
              )}
              {(i % labelEvery === 0 || b.key === selected) && (
                <text x={x + bw / 2} y={height - 6} textAnchor="middle" className={`axis${b.strong || b.key === selected ? ' strong' : ''}`}>
                  {b.label}
                </text>
              )}
              <rect
                x={padL + i * slot}
                y={0}
                width={slot}
                height={height}
                fill="transparent"
                role="button"
                tabIndex={0}
                aria-label={`${b.label || b.key}: ${v == null ? 'нет данных' : format(v)}`}
                aria-pressed={b.key === selected}
                onClick={() => onSelect(b.key === selected ? null : b.key)}
                onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(b.key === selected ? null : b.key)}
              />
            </g>
          );
        })}
        {avg != null && avg > 0 && (
          <g pointerEvents="none">
            <line x1={padL} x2={w} y1={y(avg)} y2={y(avg)} className="avg-line" />
            <text x={w - 2} y={y(avg) - 4} textAnchor="end" className="axis strong">
              в среднем {format(avg)}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}

/* ---------- Ритм суток: строка на день ---------- */

export function RhythmMap({
  rows,
  onSelect,
}: {
  rows: { date: string; label: string; strip: StripSegment[]; empty: boolean }[];
  onSelect: (date: string) => void;
}) {
  return (
    <div className="rhythm">
      <div className="rhythm-row rhythm-scale" aria-hidden="true">
        <span />
        <div className="rhythm-hours">
          {[0, 6, 12, 18, 24].map((h) => (
            <span key={h} style={{ left: `${(h / 24) * 100}%` }}>
              {h}
            </span>
          ))}
        </div>
      </div>
      {rows.map((r) => (
        <button key={r.date} type="button" className="rhythm-row" onClick={() => onSelect(r.date)} aria-label={`Открыть ${r.label}`}>
          <span className="tiny muted">{r.label}</span>
          <div className={`strip thin${r.empty ? ' empty' : ''}`}>
            {r.strip.map((s, i) => (
              <span
                key={i}
                style={{ left: `${(s.from / 1440) * 100}%`, width: `${Math.max(0.5, ((s.to - s.from) / 1440) * 100)}%`, background: `var(--c-${s.category})` }}
              />
            ))}
          </div>
        </button>
      ))}
    </div>
  );
}

export function Legend({ items }: { items: { cat: Cat; label: string }[] }) {
  return (
    <div className="legend">
      {items.map((i) => (
        <span key={i.cat}>
          <i style={{ background: color(i.cat) }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}

/* ---------- Календарь-«тепловая карта» ---------- */

/** Интенсивность 1…4 между минимумом и максимумом периода (0 — нет записей). */
function level(v: number | null, max: number, min = 0): number {
  if (v == null || max <= 0) return 0;
  const lo = Number.isFinite(min) && min < max ? min : 0;
  if (v <= lo) return 1;
  return Math.min(4, 1 + Math.floor(((v - lo) / (max - lo)) * 3.999));
}

export function MonthCalendar({
  ym,
  values,
  max,
  min = 0,
  cat,
  today,
  birthDate,
  format,
  onSelect,
  compact = false,
}: {
  ym: string;
  values: Map<string, number>;
  max: number;
  min?: number;
  cat: Cat;
  today: string;
  birthDate: string;
  format: (v: number) => string;
  onSelect: (date: string) => void;
  compact?: boolean;
}) {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  const first = `${ym}-01`;
  const days = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lead = weekday(first);
  const cells: ReactNode[] = [];
  for (let i = 0; i < lead; i++) cells.push(<span key={`l${i}`} />);
  for (let d = 1; d <= days; d++) {
    const date = `${ym}-${String(d).padStart(2, '0')}`;
    const v = values.get(date) ?? null;
    const lv = level(v, max, min);
    const off = date > today || date < birthDate;
    cells.push(
      <button
        key={date}
        type="button"
        className={`cal-cell lv${lv}${date === today ? ' today' : ''}`}
        style={{ ['--cell' as string]: color(cat) }}
        disabled={off}
        aria-label={`${d}: ${v == null ? 'нет записей' : format(v)}`}
        title={v == null ? undefined : format(v)}
        onClick={() => onSelect(date)}
      >
        {!compact && d}
      </button>,
    );
  }
  return (
    <div className={`cal${compact ? ' compact' : ''}`}>
      {!compact && (
        <div className="cal-grid cal-head" aria-hidden="true">
          {WEEKDAYS_SHORT.map((w) => (
            <span key={w}>{w}</span>
          ))}
        </div>
      )}
      <div className="cal-grid">{cells}</div>
    </div>
  );
}

export function HeatLegend({ cat }: { cat: Cat }) {
  return (
    <div className="legend heat" aria-hidden="true">
      <span>меньше</span>
      {[1, 2, 3, 4].map((l) => (
        <i key={l} className={`cal-cell lv${l}`} style={{ ['--cell' as string]: color(cat) }} />
      ))}
      <span>больше</span>
    </div>
  );
}

export function YearCalendar(props: Omit<Parameters<typeof MonthCalendar>[0], 'ym' | 'compact'> & { year: number; onMonth: (ym: string) => void }) {
  const { year, onMonth, ...rest } = props;
  return (
    <div className="year-grid">
      {Array.from({ length: 12 }, (_, i) => {
        const ym = `${year}-${String(i + 1).padStart(2, '0')}`;
        return (
          <div key={ym} className="year-month">
            <button type="button" className="tiny muted year-title" onClick={() => onMonth(ym)}>
              {MONTH_TITLES[i]}
            </button>
            <MonthCalendar ym={ym} compact {...rest} />
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Рост и вес на фоне норм ВОЗ ---------- */

export interface GrowthPoint {
  months: number;
  value: number;
  date: string;
}

export function GrowthChart({
  points,
  band,
  cat,
  format,
  maxMonths,
  height = 200,
}: {
  points: GrowthPoint[];
  /** Норма на возраст: [−2 SD, медиана, +2 SD] или null, если нет данных. */
  band: ((m: number) => [number, number, number] | null) | null;
  cat: Cat;
  format: (v: number) => string;
  maxMonths: number;
  height?: number;
}) {
  const [ref, w] = useWidth<HTMLDivElement>();
  const [sel, setSel] = useState<number | null>(null);
  useEffect(() => setSel(points.length ? points.length - 1 : null), [points]);
  const padL = 36;
  const padB = 22;
  const padT = 10;
  const plotW = Math.max(10, w - padL - 8);
  const plotH = height - padB - padT;
  const samples: { m: number; b: [number, number, number] }[] = [];
  if (band) for (let m = 0; m <= maxMonths + 1e-9; m += maxMonths / 40) {
    const b = band(m);
    if (b) samples.push({ m, b });
  }
  const vals = [...points.map((p) => p.value), ...samples.flatMap((s) => [s.b[0], s.b[2]])];
  if (!vals.length) return null;
  const lo = Math.min(...vals);
  const hi = Math.max(...vals);
  const pad = (hi - lo) * 0.08 || 1;
  const { step } = niceMax(hi - lo + 2 * pad, 4);
  const vMin = Math.floor((lo - pad) / step) * step;
  const vMax = Math.ceil((hi + pad) / step) * step;
  const x = (m: number) => padL + (m / maxMonths) * plotW;
  const y = (v: number) => padT + plotH - ((v - vMin) / (vMax - vMin)) * plotH;
  const ticks: number[] = [];
  for (let t = vMin; t <= vMax + 1e-9; t += step) ticks.push(Math.round(t * 100) / 100);
  const mTicks: number[] = [];
  const mStep = maxMonths > 12 ? 3 : maxMonths > 6 ? 2 : 1;
  for (let m = 0; m <= maxMonths; m += mStep) mTicks.push(m);
  const area = samples.length
    ? `M${samples.map((s) => `${x(s.m)},${y(s.b[2])}`).join(' L')} L${[...samples].reverse().map((s) => `${x(s.m)},${y(s.b[0])}`).join(' L')} Z`
    : '';
  const median = samples.length ? `M${samples.map((s) => `${x(s.m)},${y(s.b[1])}`).join(' L')}` : '';
  const line = points.length > 1 ? `M${points.map((p) => `${x(p.months)},${y(p.value)}`).join(' L')}` : '';
  const sp = sel != null ? points[sel] : null;

  return (
    <div ref={ref} className="chart" style={{ height }}>
      <svg width={w} height={height} role="img" aria-label="График роста с нормами ВОЗ">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={padL} x2={w - 8} y1={y(t)} y2={y(t)} className="grid" />
            <text x={padL - 6} y={y(t) + 4} textAnchor="end" className="axis">
              {String(t).replace('.', ',')}
            </text>
          </g>
        ))}
        {mTicks.map((m) => (
          <text key={m} x={x(m)} y={height - 6} textAnchor="middle" className="axis">
            {m === 0 ? '0' : `${m} мес`}
          </text>
        ))}
        {area && <path d={area} className="band" />}
        {median && <path d={median} className="median" />}
        {line && <path d={line} fill="none" stroke={color(cat)} strokeWidth={2} strokeLinejoin="round" />}
        {sp && <line x1={x(sp.months)} x2={x(sp.months)} y1={padT} y2={padT + plotH} className="crosshair" />}
        {points.map((p, i) => (
          <g key={p.date}>
            <circle cx={x(p.months)} cy={y(p.value)} r={i === sel ? 6 : 4.5} fill={color(cat)} stroke="var(--surface)" strokeWidth={2} />
            <circle
              cx={x(p.months)}
              cy={y(p.value)}
              r={16}
              fill="transparent"
              role="button"
              tabIndex={0}
              aria-label={`${p.date}: ${format(p.value)}`}
              onClick={() => setSel(i)}
              onKeyDown={(e) => e.key === 'Enter' && setSel(i)}
            />
          </g>
        ))}
      </svg>
    </div>
  );
}

/* ---------- Разбивка: горизонтальные полосы ---------- */

export function Breakdown({ items, cat }: { items: { label: string; value: number; text: string; sub?: string }[]; cat: Cat }) {
  const max = Math.max(...items.map((i) => i.value), 1);
  return (
    <div className="breakdown">
      {items.map((i) => (
        <div key={i.label} className="bd-row">
          <div className="row" style={{ gap: 8 }}>
            <span className="grow small">{i.label}</span>
            <span className="small" style={{ fontWeight: 600 }}>
              {i.text}
            </span>
          </div>
          <div className="bd-track">
            <span style={{ width: `${(i.value / max) * 100}%`, background: color(cat) }} />
          </div>
          {i.sub && <span className="tiny muted">{i.sub}</span>}
        </div>
      ))}
    </div>
  );
}
